// src/components/partners/tabs/ProfileTabShadcn.jsx
import React, { useState } from 'react';
import { Settings2, Wifi, WifiOff, Building2, Mail, Phone, MapPin, Globe, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

// shadcn components
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';

import { cn } from '../../../utils/cn';

const ProfileTabShadcn = ({
  networkProfile = {},
  partnersError = null,
  isConnected = true,
  onProfileUpdate,
  className = ''
}) => {
  const { currentUser } = useAuth();
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    business_name: networkProfile.business_name || currentUser?.business_name || '',
    business_type: networkProfile.business_type || '',
    profile_description: networkProfile.profile_description || '',
    contact_email: networkProfile.contact_email || currentUser?.email || '',
    contact_phone: networkProfile.contact_phone || '',
    business_address: networkProfile.business_address || '',
    website_url: networkProfile.website_url || '',
    is_discoverable: networkProfile.is_discoverable || false,
    accepts_new_partnerships: networkProfile.accepts_new_partnerships || true
  });

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle profile updates with better error handling
  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    try {
      setProfileLoading(true);
      setProfileError('');
      setProfileSuccess('');

      if (!isConnected) {
        setProfileError('You must be online to update your profile');
        return;
      }

      console.log('Updating profile with:', formData);

      await onProfileUpdate(formData);

      setProfileSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setProfileSuccess(''), 3000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  const clearMessages = () => {
    setProfileSuccess('');
    setProfileError('');
  };

  // Calculate profile completeness
  const profileCompleteness = React.useMemo(() => {
    const fields = [
      formData.business_name,
      formData.business_type,
      formData.profile_description && formData.profile_description.length > 50,
      formData.contact_email,
      formData.contact_phone,
      formData.business_address,
      formData.website_url
    ];

    const completedFields = fields.filter(Boolean).length;
    return Math.round((completedFields / fields.length) * 100);
  }, [formData]);

  return (
    <div className={cn("space-y-6", className)}>
      {/* Connection Status Warning */}
      {!isConnected && (
        <Alert className="border-amber-500/20 bg-amber-500/10">
          <WifiOff className="h-4 w-4" />
          <AlertDescription>
            <div>
              <p className="font-medium">You're currently offline</p>
              <p className="text-sm">Connect to the internet to update your network profile.</p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Success Messages */}
      {profileSuccess && (
        <Alert className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
          <AlertDescription className="flex items-center justify-between">
            <span>{profileSuccess}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Error Messages */}
      {(profileError || partnersError) && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>{profileError || partnersError}</span>
            <Button variant="ghost" size="sm" onClick={clearMessages}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Profile Completeness Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Profile Completeness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Profile Completion</span>
              <Badge variant={profileCompleteness >= 70 ? "default" : "secondary"}>
                {profileCompleteness}%
              </Badge>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${profileCompleteness}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {profileCompleteness >= 70
                ? "Your profile looks great! A complete profile attracts more partners."
                : "Complete your profile to improve discoverability and attract potential partners."
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Profile Form */}
      <form onSubmit={handleProfileUpdate} className="space-y-6">
        {/* Business Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Business Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => handleFieldChange('business_name', e.target.value)}
                  placeholder="Your business name"
                  disabled={!isConnected}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="business_type">Business Type</Label>
                <Input
                  id="business_type"
                  value={formData.business_type}
                  onChange={(e) => handleFieldChange('business_type', e.target.value)}
                  placeholder="e.g., Restaurant, Salon, Consulting"
                  disabled={!isConnected}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile_description">Business Description</Label>
              <Textarea
                id="profile_description"
                value={formData.profile_description}
                onChange={(e) => handleFieldChange('profile_description', e.target.value)}
                placeholder="Describe your business, services, and what makes you unique..."
                rows={4}
                disabled={!isConnected}
              />
              <p className="text-xs text-muted-foreground">
                {formData.profile_description.length}/500 characters
                {formData.profile_description.length < 50 && " (Minimum 50 characters recommended)"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email *</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleFieldChange('contact_email', e.target.value)}
                  placeholder="business@example.com"
                  disabled={!isConnected}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => handleFieldChange('contact_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  disabled={!isConnected}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_address">Business Address</Label>
              <Input
                id="business_address"
                value={formData.business_address}
                onChange={(e) => handleFieldChange('business_address', e.target.value)}
                placeholder="123 Main St, City, State 12345"
                disabled={!isConnected}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                type="url"
                value={formData.website_url}
                onChange={(e) => handleFieldChange('website_url', e.target.value)}
                placeholder="https://www.yourbusiness.com"
                disabled={!isConnected}
              />
            </div>
          </CardContent>
        </Card>

        {/* Partnership Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Partnership Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  {formData.is_discoverable ? (
                    <Eye className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Label htmlFor="is_discoverable" className="text-base">
                    Make Business Discoverable
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Allow other businesses to find and send connection requests to you
                </p>
              </div>
              <Switch
                id="is_discoverable"
                checked={formData.is_discoverable}
                onCheckedChange={(checked) => handleFieldChange('is_discoverable', checked)}
                disabled={!isConnected}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="accepts_new_partnerships" className="text-base">
                  Accept New Partnership Requests
                </Label>
                <p className="text-sm text-muted-foreground">
                  Receive notifications when businesses want to partner with you
                </p>
              </div>
              <Switch
                id="accepts_new_partnerships"
                checked={formData.accepts_new_partnerships}
                onCheckedChange={(checked) => handleFieldChange('accepts_new_partnerships', checked)}
                disabled={!isConnected}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={profileLoading || !isConnected}
            className="min-w-[120px]"
          >
            {profileLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-background border-t-foreground rounded-full animate-spin"></div>
                Saving...
              </div>
            ) : (
              'Save Profile'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileTabShadcn;